import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, set } from 'date-fns';
import { useStore } from '../../context/StoreContext';
import { Button } from '../../components/ui/Button';
import { calculatePrice } from '../../utils/pricingEngine';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { cn, formatCurrency, parseLocalDate } from '../../lib/utils';
import { sendManualInvoice, triggerBookingConfirmedWebhook } from '../../lib/api';
import { useSearchParams } from 'react-router-dom';

const steps = ['Event Type', 'Date & Time', 'Add-ons', 'Review'];

const generateTimeSlots = (startHour = 0, endHour = 24) => {
  const slots = [];
  for (let i = 0; i < 24 * 2; i++) {
    const minutes = i * 30;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < startHour || hours >= endHour) continue;
    if (hours === endHour && mins > 0) continue;
    const timeString = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    const date = set(new Date(), { hours, minutes: mins, seconds: 0 });
    const label = format(date, 'h:mm a');
    slots.push({ value: timeString, label });
  }
  return slots;
};

const BookingWizard = ({ isModal = false, adminMode = false, initialDate = null, onClose = null }) => {
  const [searchParams] = useSearchParams();
  const {
    eventTypes, pricingRules, addOns,
    getAddOnsForEventType, getCategoriesForEventType,
    addBooking, settings, getBookingsForDate
  } = useStore();
  const isFromCalendar = searchParams.get('from') === 'calendar';
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showPolicies, setShowPolicies] = useState(false);
  const [hasViewedPolicies, setHasViewedPolicies] = useState(false);
  const [agreedToPolicies, setAgreedToPolicies] = useState(false);
  const [formData, setFormData] = useState({
    eventType: null,
    date: initialDate || format(new Date(), 'yyyy-MM-dd'),
    startTime: '10:00',
    endTime: '14:00',
    selectedAddOns: [],
    guestCount: 50,
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    descriptionOfUse: '',
    notes: '',
    isOfflinePayment: false,
    offlineDepositPaid: false,
    offlineDepositAmount: ''
  });
  const [pricing, setPricing] = useState({ subtotal: 0, addOnsTotal: 0, totalPrice: 0, depositAmount: 0, balanceDue: 0 });
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [timeConflict, setTimeConflict] = useState(null);

  const opStart = settings?.operatingHoursStart || '08:00';
  const opEnd = settings?.operatingHoursEnd || '22:00';
  const startHour = parseInt(opStart.split(':')[0], 10);
  const endHour = parseInt(opEnd.split(':')[0], 10);

  const TIME_SLOTS = useMemo(() => generateTimeSlots(startHour, endHour), [startHour, endHour]);
  const END_TIME_SLOTS = useMemo(() => {
    const slots = generateTimeSlots(startHour, endHour + 1);
    return slots.filter(s => s.value > opStart && s.value <= opEnd);
  }, [startHour, endHour, opStart, opEnd]);

  useEffect(() => {
    const eventTypeId = searchParams.get('eventType');
    const dateParam = searchParams.get('date');
    if (eventTypeId) {
      const selectedType = eventTypes.find(et => et.id === eventTypeId);
      if (selectedType) {
        setFormData(prev => ({ ...prev, eventType: selectedType }));
        setCurrentStep(1);
      }
    }
    if (dateParam) {
      try {
        const parsedDate = parseLocalDate(dateParam);
        if (!isNaN(parsedDate.getTime())) {
          setFormData(prev => ({ ...prev, date: format(parsedDate, 'yyyy-MM-dd') }));
          if (!eventTypeId) setCurrentStep(0);
        }
      } catch (e) {
        console.error('Invalid date parameter');
      }
    }
  }, [searchParams, eventTypes]);

  const unavailableTimes = useMemo(() => {
    if (!formData.date) return [];
    const bookings = getBookingsForDate(formData.date);
    return bookings.map(b => {
      const start = new Date(`${b.date}T${b.startTime}`);
      const end = new Date(`${b.date}T${b.endTime}`);
      return {
        start,
        end,
        display: `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`
      };
    });
  }, [formData.date, getBookingsForDate]);

  useEffect(() => {
    if (formData.eventType) {
      const addonsForType = getAddOnsForEventType(formData.eventType.id);
      setAvailableAddOns(addonsForType);
      const catsForType = getCategoriesForEventType(formData.eventType.id);
      setAvailableCategories(catsForType);
      const bundledIds = formData.eventType.bundledAddons || [];
      setFormData(prev => {
        const filtered = prev.selectedAddOns.filter(id =>
          addonsForType.some(addon => addon.id === id)
        );
        const merged = [...new Set([...bundledIds, ...filtered])];
        return { ...prev, selectedAddOns: merged };
      });
    }
  }, [formData.eventType, getAddOnsForEventType, getCategoriesForEventType]);

  useEffect(() => {
    if (!formData.eventType || !formData.date || !formData.startTime || !formData.endTime) return;
    const start = new Date(`${formData.date}T${formData.startTime}`);
    const end = new Date(`${formData.date}T${formData.endTime}`);
    if (end <= start) return;

    const result = calculatePrice(
      formData.eventType, start, end, pricingRules,
      availableAddOns, formData.selectedAddOns
    );
    result.depositAmount = Math.round(result.subtotal * 0.5 * 100) / 100;
    result.balanceDue = Math.round((result.totalPrice - result.depositAmount) * 100) / 100;
    setPricing(result);
  }, [formData, pricingRules, availableAddOns, settings]);

  useEffect(() => {
    if (!formData.eventType || !formData.date || !formData.startTime || !formData.endTime) {
      setTimeConflict(null);
      return;
    }

    const existingBookings = getBookingsForDate(formData.date);
    const requestedStart = new Date(`${formData.date}T${formData.startTime}`);
    const requestedEnd = new Date(`${formData.date}T${formData.endTime}`);
    const cooldownHours = formData.eventType.cooldownHours ?? 1;

    for (const booking of existingBookings) {
      const bookingStart = new Date(`${booking.date}T${booking.startTime}`);
      const bookingEnd = new Date(`${booking.date}T${booking.endTime}`);
      const bookingEndWithCooldown = new Date(bookingEnd.getTime() + cooldownHours * 60 * 60 * 1000);

      const hasConflict =
        (requestedStart >= bookingStart && requestedStart < bookingEndWithCooldown) ||
        (requestedEnd > bookingStart && requestedEnd <= bookingEndWithCooldown) ||
        (requestedStart <= bookingStart && requestedEnd >= bookingEndWithCooldown);

      if (hasConflict) {
        setTimeConflict({
          bookingEnd: format(bookingEnd, 'h:mm a'),
          cooldownHours,
          earliestStart: format(bookingEndWithCooldown, 'h:mm a'),
          eventName: booking.eventType?.name || 'Another event'
        });
        return;
      }
    }
    setTimeConflict(null);
  }, [formData.date, formData.startTime, formData.endTime, formData.eventType, getBookingsForDate]);

  const containerRef = useRef(null);

  const broadcastHeight = useCallback(() => {
    const height = document.documentElement.scrollHeight;
    window.parent.postMessage({ type: 'WIDGET_RESIZE', height }, '*');
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => broadcastHeight());
    observer.observe(containerRef.current);
    broadcastHeight();
    return () => observer.disconnect();
  }, [broadcastHeight]);

  useEffect(() => {
    const timer = setTimeout(() => broadcastHeight(), 250);
    return () => clearTimeout(timer);
  }, [currentStep, completed, broadcastHeight]);

  const isDateTimeValid = () => {
    if (!formData.date || !formData.startTime || !formData.endTime) return false;
    const start = new Date(`${formData.date}T${formData.startTime}`);
    const end = new Date(`${formData.date}T${formData.endTime}`);
    if (end <= start) return false;
    if (timeConflict) return false;
    if (formData.startTime < opStart || formData.endTime > opEnd) return false;
    if (formData.eventType && !formData.eventType.isFlatPackage) {
      const durationHours = (end - start) / (1000 * 60 * 60);
      if (durationHours < formData.eventType.minDuration) return false;
    }
    return true;
  };

  const getDurationHours = () => {
    if (!formData.startTime || !formData.endTime) return 0;
    const start = new Date(`${formData.date}T${formData.startTime}`);
    const end = new Date(`${formData.date}T${formData.endTime}`);
    return (end - start) / (1000 * 60 * 60);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep(c => c + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(c => c - 1);
  };

  const [submitError, setSubmitError] = useState('');
  const [redirectingToPayment, setRedirectingToPayment] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError('');

    try {
      const offlineDepositPaid = adminMode && formData.offlineDepositPaid;
      const offlineDepositAmount = offlineDepositPaid
        ? (Number(formData.offlineDepositAmount) || 0)
        : 0;

      const createdBooking = await addBooking({
        ...formData,
        totalPrice: pricing.totalPrice,
        offlineDepositPaid,
        offlineDepositAmount,
        agreedToPolicies: true,
        policiesViewedAt: new Date().toISOString(),
        acceptedPolicySnapshot: settings?.rentalPolicies?.masterPolicy || ''
      });

      const isAutoApproved = formData.eventType?.requiresAdminApproval === false;

      if (offlineDepositPaid) {
        triggerConfirmationEmailForOffline(createdBooking.id);
        setCompleted(true);
      } else if (formData.isOfflinePayment) {
        if (isAutoApproved) {
          triggerConfirmationEmailForOffline(createdBooking.id);
          triggerBookingConfirmedWebhook(createdBooking.id);
        }
        setCompleted(true);
      } else if (isAutoApproved) {
        setRedirectingToPayment(true);
        try {
          const result = await sendManualInvoice(createdBooking.id, 'deposit', { autoApproved: true });
          if (result.paymentLink) {
            window.top.location.href = result.paymentLink;
            return;
          }
          setRedirectingToPayment(false);
          setSubmitError('Your booking was saved, but our payment processor failed to load. We will email you an invoice shortly.');
        } catch {
          setRedirectingToPayment(false);
          setSubmitError('Your booking was saved, but our payment processor failed to load. We will email you an invoice shortly.');
        }
      } else {
        setCompleted(true);
      }
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit booking. Please try again.');
    } finally {
      if (!redirectingToPayment) {
        setLoading(false);
      }
    }
  };

  const triggerConfirmationEmailForOffline = (bookingId) => {
    const headers = {
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    };
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-confirmation-email`, {
      method: 'POST', headers, body: JSON.stringify({ bookingId }),
    }).catch(() => {});
  };

  const isAddOnsValid = () => {
    for (const cat of availableCategories) {
      if (cat.required && cat.selectionMode === 'single') {
        const catAddOns = availableAddOns.filter(a => a.categoryId === cat.id);
        const hasSelection = catAddOns.some(a => formData.selectedAddOns.includes(a.id));
        if (!hasSelection) return false;
      }
    }
    return true;
  };

  const isReviewStepValid = () => {
    return (
      formData.contactName.trim() !== '' &&
      formData.contactEmail.trim() !== '' &&
      formData.contactPhone.trim() !== '' &&
      formData.guestCount > 0 &&
      formData.descriptionOfUse.trim() !== '' &&
      hasViewedPolicies &&
      agreedToPolicies
    );
  };

  const handleAddOnToggle = (addon, category) => {
    const bundledIds = formData.eventType?.bundledAddons || [];
    if (bundledIds.includes(addon.id)) return;

    if (category && category.selectionMode === 'single') {
      const catAddOns = availableAddOns.filter(a => a.categoryId === category.id);
      const otherCatIds = catAddOns.map(a => a.id);
      setFormData(prev => ({
        ...prev,
        selectedAddOns: [
          ...prev.selectedAddOns.filter(id => !otherCatIds.includes(id)),
          addon.id
        ]
      }));
    } else {
      const isSelected = formData.selectedAddOns.includes(addon.id);
      setFormData(prev => ({
        ...prev,
        selectedAddOns: isSelected
          ? prev.selectedAddOns.filter(id => id !== addon.id)
          : [...prev.selectedAddOns, addon.id]
      }));
    }
  };

  const PoliciesModal = () => (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
          <h3 className="text-2xl font-bold text-gray-900">Rental Agreement</h3>
          <button
            onClick={() => { setShowPolicies(false); setHasViewedPolicies(true); }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {settings?.rentalPolicies?.masterPolicy ||
              "No rental agreement has been configured. Please contact the venue for terms and conditions."}
          </p>
        </div>
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <Button
            onClick={() => { setShowPolicies(false); setHasViewedPolicies(true); }}
            className="w-full font-bold"
          >
            I Have Read the Agreement
          </Button>
        </div>
      </div>
    </div>
  );

  if (completed) {
    const homeUrl = isFromCalendar ? '/calendar' : '/';
    const isAutoApproved = formData.eventType?.requiresAdminApproval === false;

    let confirmation;
    if (formData.offlineDepositPaid) {
      confirmation = {
        title: 'Booking Confirmed!',
        message: "<p>The deposit was recorded as paid in person and the booking is confirmed.</p>",
        buttons: [{ label: 'Back to Home', url: homeUrl, style: 'primary' }]
      };
    } else if (formData.isOfflinePayment) {
      confirmation = {
        title: 'Booking Confirmed!',
        message: "<p>Your booking has been successfully created. The venue has been booked.</p>",
        buttons: [{ label: 'Back to Home', url: homeUrl, style: 'primary' }]
      };
    } else if (formData.eventType?.confirmationPage?.title && formData.eventType.requiresAdminApproval !== false) {
      confirmation = formData.eventType.confirmationPage;
    } else if (isAutoApproved) {
      confirmation = {
        title: 'Booking Confirmed!',
        message: "<p>Your booking has been confirmed. You'll receive a confirmation email with details shortly.</p>",
        buttons: [{ label: 'Back to Home', url: homeUrl, style: 'primary' }]
      };
    } else {
      confirmation = {
        title: 'Request Received!',
        message: "<p>We've received your booking request. An admin will review it shortly. You'll receive an email update soon.</p>",
        buttons: [{ label: 'Back to Home', url: homeUrl, style: 'primary' }]
      };
    }

    if (isModal) {
      return (
        <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <SafeIcon icon={FiIcons.FiCheck} className="text-4xl text-green-700" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{confirmation.title === 'Request Received!3' ? 'Request Received!' : confirmation.title}</h2>
          <div
            className="text-gray-700 mb-8 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: confirmation.message }}
          />
          <Button onClick={() => onClose && onClose()} className="min-w-[140px]">Done</Button>
        </div>
      );
    }

    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center max-w-2xl mx-auto">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <SafeIcon icon={FiIcons.FiCheck} className="text-4xl text-green-700" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{confirmation.title === 'Request Received!3' ? 'Request Received!' : confirmation.title}</h2>
        <div
          className="text-gray-700 mb-8 prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: confirmation.message }}
        />
        <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
          {confirmation.buttons?.map((btn, idx) => {
            const btnUrl = isFromCalendar && (btn.url === '/' || btn.url === '') ? '/calendar' : btn.url;
            return (
              <Button
                key={idx}
                variant={btn.style === 'outline' ? 'outline' : 'primary'}
                onClick={() => window.location.href = btnUrl}
                className="min-w-[140px]"
              >
                {btn.label}
              </Button>
            );
          })}
          {(!confirmation.buttons || confirmation.buttons.length === 0) && (
            <Button onClick={() => window.location.href = homeUrl}>Back to Home</Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={isModal ? "px-1 pt-2 pb-2" : "max-w-2xl mx-auto px-4 pt-6 pb-24"}>
      <div className="flex justify-between mb-8 relative">
        <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 -z-10 -translate-y-1/2" />
        {steps.map((step, idx) => (
          <div key={idx} className="flex flex-col items-center gap-2 bg-white px-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors border-2",
              idx <= currentStep
                ? "bg-primary text-white border-primary"
                : "bg-white text-gray-500 border-gray-300"
            )}>
              {idx + 1}
            </div>
            <span className={cn(
              "text-xs font-bold hidden sm:block",
              idx <= currentStep ? "text-primary" : "text-gray-500"
            )}>{step}</span>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {currentStep === 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold mb-4 text-gray-900">What kind of event is it?</h2>
              <div className="grid gap-4">
                {(adminMode ? eventTypes : eventTypes.filter(et => et.active)).map(type => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, eventType: type }));
                      handleNext();
                    }}
                    className={cn(
                      "rounded-xl border-2 text-left transition-all hover:shadow-md w-full overflow-hidden",
                      formData.eventType?.id === type.id
                        ? "border-primary bg-blue-50"
                        : "border-gray-200 bg-white hover:border-blue-300"
                    )}
                  >
                    {type.imageUrl ? (
                      <div className="aspect-video w-full bg-gray-100 relative overflow-hidden">
                        <img
                          src={type.imageUrl}
                          alt={type.name}
                          className="w-full h-full object-cover"
                          onError={e => { e.target.parentElement.style.display = 'none'; }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-video w-full flex items-center justify-center" style={{ backgroundColor: `${type.color}10` }}>
                        <SafeIcon icon={FiIcons.FiCalendar} className="text-4xl" style={{ color: type.color }} />
                      </div>
                    )}
                    <div className="p-4 flex items-center">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-gray-900 text-lg">{type.name}</h3>
                          {adminMode && !type.active && (
                            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                              Admin Only
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 font-medium">{type.description}</p>
                      </div>
                      <div className="text-right pl-4">
                        {type.isFlatPackage ? (
                          <>
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap block">Flat Rate</span>
                            <span className="block font-bold text-primary text-lg">
                              {formatCurrency(type.flatPackagePrice)}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap block">Starting at</span>
                            <span className="block font-bold text-primary text-lg">
                              {formatCurrency(type.baseRate)}/hr
                            </span>
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Min {type.minDuration} hrs</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Select date & time</h2>

              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-lg font-bold text-gray-900">Date</label>
                  <div className="text-sm text-gray-500 font-medium">
                    {format(parseLocalDate(formData.date), 'MMMM yyyy')}
                  </div>
                </div>
                <input
                  type="date"
                  className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary outline-none bg-gray-50 text-gray-900 font-bold text-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  min={format(new Date(), 'yyyy-MM-dd')}
                  value={formData.date}
                  onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))}
                />
                {unavailableTimes.length > 0 && (
                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-400 font-medium mb-2">Space is unavailable:</p>
                    <div className="space-y-1">
                      {unavailableTimes.map((time, idx) => (
                        <p key={idx} className="text-sm font-medium text-gray-500">{time.display}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1">
                  <label className="block text-center text-sm font-bold text-gray-400 mb-2 uppercase tracking-wider">Start</label>
                  <select
                    value={formData.startTime}
                    onChange={(e) => setFormData(p => ({ ...p, startTime: e.target.value }))}
                    className="w-full p-4 bg-gray-50 border-2 border-transparent hover:border-gray-200 rounded-xl text-center font-bold text-base sm:text-xl text-gray-900 focus:ring-2 focus:ring-primary focus:bg-white outline-none appearance-none cursor-pointer transition-all"
                  >
                    {TIME_SLOTS.map(slot => (
                      <option key={`start-${slot.value}`} value={slot.value}>{slot.label}</option>
                    ))}
                  </select>
                </div>
                <div className="text-gray-400 font-medium hidden sm:block sm:pt-6">to</div>
                <div className="flex-1">
                  <label className="block text-center text-sm font-bold text-gray-400 mb-2 uppercase tracking-wider">End</label>
                  <select
                    value={formData.endTime}
                    onChange={(e) => setFormData(p => ({ ...p, endTime: e.target.value }))}
                    className="w-full p-4 bg-gray-50 border-2 border-transparent hover:border-gray-200 rounded-xl text-center font-bold text-base sm:text-xl text-gray-900 focus:ring-2 focus:ring-primary focus:bg-white outline-none appearance-none cursor-pointer transition-all"
                  >
                    {END_TIME_SLOTS.filter(s => s.value > formData.startTime).map(slot => (
                      <option key={`end-${slot.value}`} value={slot.value}>{slot.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                {timeConflict && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-600 text-xl flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-red-900">Time Conflict</p>
                      <p className="text-xs text-red-700 mt-1 leading-relaxed">
                        {timeConflict.eventName} ends at {timeConflict.bookingEnd}.<br />
                        With the {timeConflict.cooldownHours}-hour cooldown, earliest start is <strong>{timeConflict.earliestStart}</strong>.
                      </p>
                    </div>
                  </div>
                )}
                {!timeConflict && isDateTimeValid() && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiCheck} className="text-green-600 text-xl flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-green-900">
                        {getDurationHours().toFixed(1)} hours
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Valid duration for {formData.eventType?.name}
                      </p>
                    </div>
                  </div>
                )}
                {!timeConflict && getDurationHours() <= 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                    <SafeIcon icon={FiIcons.FiAlertCircle} className="text-xl flex-shrink-0" />
                    <span className="text-sm font-medium">End time must be after start time</span>
                  </div>
                )}
                {!timeConflict && getDurationHours() > 0 && !formData.eventType?.isFlatPackage && getDurationHours() < (formData.eventType?.minDuration || 0) && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-amber-900">Duration too short</p>
                      <p className="text-xs text-amber-700 mt-1">
                        Minimum {formData.eventType?.minDuration} hours required.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-center gap-2">
                <p className="text-xs text-gray-500 text-center">
                  Operating hours: {opStart.replace(/^0/, '')} AM - {opEnd.replace(/^0/, '')} PM. Times include setup and tear down.
                </p>
                <button type="button" onClick={() => setShowPolicies(true)} className="text-primary hover:text-blue-700 transition-colors" title="View rental policies">
                  <SafeIcon icon={FiIcons.FiInfo} className="text-base" />
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Enhance your event</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Options available for <span className="font-bold text-gray-900">{formData.eventType?.name}</span>
                </p>
              </div>

              {(() => {
                const bundledIds = formData.eventType?.bundledAddons || [];
                const bundledAddOnItems = bundledIds.map(id => availableAddOns.find(a => a.id === id)).filter(Boolean);
                const optionalAddOns = availableAddOns.filter(a => !bundledIds.includes(a.id));
                const optionalCategorized = optionalAddOns.filter(a => a.categoryId);
                const optionalUncategorized = optionalAddOns.filter(a => !a.categoryId);

                return (
                  <>
                    {bundledAddOnItems.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-gray-800">Included with {formData.eventType?.name}</h3>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">Bundled</span>
                        </div>
                        <div className="grid gap-3">
                          {bundledAddOnItems.map(addon => (
                            <div
                              key={addon.id}
                              className="flex items-center justify-between p-4 rounded-xl border-2 border-green-200 bg-green-50 opacity-90"
                            >
                              <div className="flex items-center gap-3">
                                {addon.imageUrl && (
                                  <img src={addon.imageUrl} alt={addon.name} className="w-12 h-12 object-cover rounded-lg" />
                                )}
                                <div className="w-5 h-5 rounded-full bg-green-600 border-green-600 border flex items-center justify-center flex-shrink-0">
                                  <SafeIcon icon={FiIcons.FiLock} className="text-white text-[10px]" />
                                </div>
                                <div>
                                  <h4 className="font-bold text-gray-900">{addon.name}</h4>
                                  <p className="text-sm text-gray-600 font-medium">{addon.description}</p>
                                </div>
                              </div>
                              <div className="text-right pl-2">
                                <span className="font-bold block text-gray-900">{formatCurrency(addon.price)}</span>
                                <span className="text-xs text-green-700 font-bold whitespace-nowrap">Included</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {availableCategories.length === 0 && optionalUncategorized.length === 0 && bundledAddOnItems.length === 0 ? (
                      <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                        <SafeIcon icon={FiIcons.FiPackage} className="mx-auto text-4xl text-gray-300 mb-2" />
                        <p className="text-gray-500 font-medium">No add-ons available for this event type.</p>
                        <p className="text-xs text-gray-400 mt-1">You can skip this step and continue.</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {availableCategories.map(cat => {
                          const catAddOns = optionalCategorized.filter(a => a.categoryId === cat.id);
                          if (catAddOns.length === 0) return null;
                          return (
                            <div key={cat.id} className="space-y-3">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-gray-800">{cat.name}</h3>
                                {cat.required && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">Required</span>}
                                {cat.selectionMode === 'single' && <span className="text-xs text-gray-500 font-medium">Select one</span>}
                              </div>
                              {cat.description && <p className="text-sm text-gray-600">{cat.description}</p>}
                              <div className="grid gap-3">
                                {catAddOns.map(addon => {
                                  const isSelected = formData.selectedAddOns.includes(addon.id);
                                  return (
                                    <div
                                      key={addon.id}
                                      onClick={() => handleAddOnToggle(addon, cat)}
                                      className={cn(
                                        "flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all",
                                        isSelected ? "border-primary bg-blue-50" : "border-gray-200 bg-white hover:border-blue-300"
                                      )}
                                    >
                                      <div className="flex items-center gap-3">
                                        {addon.imageUrl && (
                                          <img src={addon.imageUrl} alt={addon.name} className="w-12 h-12 object-cover rounded-lg" />
                                        )}
                                        <div className={cn("w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0",
                                          isSelected ? "bg-primary border-primary" : "border-gray-400 bg-white"
                                        )}>
                                          {isSelected && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                                        </div>
                                        <div>
                                          <h4 className="font-bold text-gray-900">{addon.name}</h4>
                                          <p className="text-sm text-gray-600 font-medium">{addon.description}</p>
                                        </div>
                                      </div>
                                      <div className="text-right pl-2">
                                        <span className="font-bold block text-gray-900">{formatCurrency(addon.price)}</span>
                                        <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{addon.type === 'hourly' ? '/hr' : 'flat'}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}

                        {optionalUncategorized.length > 0 && (
                          <div className="space-y-3">
                            {availableCategories.length > 0 && <h3 className="font-bold text-gray-800">Other Options</h3>}
                            <div className="grid gap-3">
                              {optionalUncategorized.map(addon => {
                                const isSelected = formData.selectedAddOns.includes(addon.id);
                                return (
                                  <div
                                    key={addon.id}
                                    onClick={() => handleAddOnToggle(addon, null)}
                                    className={cn(
                                      "flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all",
                                      isSelected ? "border-primary bg-blue-50" : "border-gray-200 bg-white hover:border-blue-300"
                                    )}
                                  >
                                    <div className="flex items-center gap-3">
                                      {addon.imageUrl && (
                                        <img src={addon.imageUrl} alt={addon.name} className="w-12 h-12 object-cover rounded-lg" />
                                      )}
                                      <div className={cn("w-5 h-5 rounded border flex items-center justify-center flex-shrink-0",
                                        isSelected ? "bg-primary border-primary" : "border-gray-400 bg-white"
                                      )}>
                                        {isSelected && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                                      </div>
                                      <div>
                                        <h4 className="font-bold text-gray-900">{addon.name}</h4>
                                        <p className="text-sm text-gray-600 font-medium">{addon.description}</p>
                                      </div>
                                    </div>
                                    <div className="text-right pl-2">
                                      <span className="font-bold block text-gray-900">{formatCurrency(addon.price)}</span>
                                      <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{addon.type === 'hourly' ? '/hr' : 'flat'}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900">Final Details</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                    <input type="text" placeholder="Enter your name"
                      className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      value={formData.contactName}
                      onChange={e => setFormData(p => ({ ...p, contactName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                    <input type="tel" placeholder="(555) 123-4567"
                      className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      value={formData.contactPhone}
                      onChange={e => setFormData(p => ({ ...p, contactPhone: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                  <input type="email" placeholder="your.email@example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    value={formData.contactEmail}
                    onChange={e => setFormData(p => ({ ...p, contactEmail: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Number of Guests <span className="text-red-500">*</span></label>
                  <input type="number" min="1" placeholder="50"
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    value={formData.guestCount}
                    onChange={e => setFormData(p => ({ ...p, guestCount: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Description of Use <span className="text-red-500">*</span></label>
                  <textarea placeholder="Please describe how you plan to use the venue..."
                    className="w-full p-3 border border-gray-300 rounded-lg h-24 bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    value={formData.descriptionOfUse}
                    onChange={e => setFormData(p => ({ ...p, descriptionOfUse: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Additional Notes</label>
                  <textarea placeholder="Any other details or special requests..."
                    className="w-full p-3 border border-gray-300 rounded-lg h-20 bg-white text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    value={formData.notes}
                    onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                  />
                </div>
              </div>

              <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-4">
                  <SafeIcon icon={FiIcons.FiFileText} className="text-amber-600 text-xl mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-2">Rental Policies Agreement</h4>
                    <p className="text-sm text-gray-700 mb-3">
                      Before submitting your booking request, you must read and agree to our rental policies.
                    </p>
                    <button type="button" onClick={() => setShowPolicies(true)}
                      className="inline-flex items-center gap-2 text-primary font-bold text-sm hover:underline"
                    >
                      <SafeIcon icon={FiIcons.FiExternalLink} />
                      Read Rental Policies
                      {hasViewedPolicies && (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs ml-2">
                          <SafeIcon icon={FiIcons.FiCheck} className="text-xs" /> Viewed
                        </span>
                      )}
                    </button>
                  </div>
                </div>
                {hasViewedPolicies && (
                  <div
                    onClick={() => setAgreedToPolicies(!agreedToPolicies)}
                    className="flex items-start gap-3 p-4 bg-white rounded-lg border-2 border-gray-200 cursor-pointer hover:border-primary transition-colors"
                  >
                    <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5",
                      agreedToPolicies ? "bg-primary border-primary" : "border-gray-400 bg-white"
                    )}>
                      {agreedToPolicies && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                    </div>
                    <label className="text-sm font-medium text-gray-900 cursor-pointer">
                      I have read and agree to the rental policies and terms outlined within the agreement.
                    </label>
                  </div>
                )}
                {!hasViewedPolicies && (
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-100 p-3 rounded-lg mt-3">
                    <SafeIcon icon={FiIcons.FiAlertCircle} className="flex-shrink-0 mt-0.5" />
                    <span>You must click "Read Rental Policies" above before you can submit your request.</span>
                  </div>
                )}
              </div>

              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-600 text-xl flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-900">Booking Failed</p>
                    <p className="text-sm text-red-700 mt-1">{submitError}</p>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-xl space-y-3 border border-gray-200">
                <h3 className="font-bold text-gray-900 border-b border-gray-200 pb-2">Booking Summary</h3>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Event Type</span>
                  <span className="font-bold text-gray-900">{formData.eventType?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Date</span>
                  <span className="font-bold text-gray-900">{format(parseLocalDate(formData.date), 'MMMM do, yyyy')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Time</span>
                  <span className="font-bold text-gray-900">
                    {TIME_SLOTS.find(t => t.value === formData.startTime)?.label || formData.startTime} - {END_TIME_SLOTS.find(t => t.value === formData.endTime)?.label || formData.endTime}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Duration</span>
                  <span className="font-bold text-gray-900">{getDurationHours().toFixed(1)} hours</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 font-medium">Guests</span>
                  <span className="font-bold text-gray-900">{formData.guestCount}</span>
                </div>

                <div className="border-t border-gray-200 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 font-medium">Venue Rental</span>
                    <span className="font-bold text-gray-900">{formatCurrency(pricing.subtotal)}</span>
                  </div>
                  {formData.selectedAddOns.length > 0 && (
                    <div className="space-y-1">
                      {formData.selectedAddOns.map(addonId => {
                        const addon = availableAddOns.find(a => a.id === addonId);
                        if (!addon) return null;
                        const isBundled = (formData.eventType?.bundledAddons || []).includes(addonId);
                        const addonCost = addon.type === 'hourly' ? addon.price * getDurationHours() : addon.price;
                        return (
                          <div key={addonId} className="flex justify-between text-sm">
                            <span className="text-gray-600 font-medium">
                              {addon.name}
                              {isBundled && <span className="text-xs text-green-700 font-bold ml-1">(Bundled)</span>}
                            </span>
                            <span className="font-bold text-gray-900">{formatCurrency(addonCost)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="font-bold text-lg text-gray-900">Total</span>
                    <span className="font-bold text-2xl text-primary">{formatCurrency(pricing.totalPrice)}</span>
                  </div>
                  {!formData.isOfflinePayment && !formData.offlineDepositPaid && (
                    <div className="flex justify-between text-sm bg-blue-50 p-3 rounded-lg -mx-1">
                      <span className="text-blue-800 font-medium">
                        {formData.eventType?.requiresAdminApproval !== false
                          ? 'Deposit Due Upon Approval (50% of venue rental)'
                          : 'Deposit Due (50% of venue rental)'}
                      </span>
                      <span className="font-bold text-blue-900">{formatCurrency(pricing.depositAmount)}</span>
                    </div>
                  )}
                </div>
                {!formData.isOfflinePayment && !formData.offlineDepositPaid && (
                  <p className="text-xs text-gray-500 font-medium text-center mt-2">
                    {formData.eventType?.requiresAdminApproval !== false
                      ? 'An admin will review your request. Once approved, you will receive a payment link for the deposit.'
                      : 'Your booking will be confirmed immediately. You will receive a confirmation email shortly.'}
                  </p>
                )}
              </div>

              {formData.eventType?.allowOfflinePayment && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5">
                  <div
                    onClick={() => setFormData(p => ({
                      ...p,
                      isOfflinePayment: !p.isOfflinePayment,
                      offlineDepositPaid: !p.isOfflinePayment ? false : p.offlineDepositPaid,
                      offlineDepositAmount: !p.isOfflinePayment ? '' : p.offlineDepositAmount
                    }))}
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5",
                      formData.isOfflinePayment ? "bg-primary border-primary" : "border-gray-400 bg-white"
                    )}>
                      {formData.isOfflinePayment && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-900 cursor-pointer">
                        I will arrange payment directly with the venue (Pay by check, cash, or alternative method).
                      </label>
                      <p className="text-xs text-amber-700 mt-1">Selecting this option will skip the online payment step.</p>
                    </div>
                  </div>
                </div>
              )}

              {adminMode && !formData.isOfflinePayment && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5">
                  <div
                    onClick={() => setFormData(p => ({
                      ...p,
                      offlineDepositPaid: !p.offlineDepositPaid,
                      offlineDepositAmount: !p.offlineDepositPaid
                        ? String(pricing.depositAmount)
                        : p.offlineDepositAmount
                    }))}
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    <div className={cn("w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5",
                      formData.offlineDepositPaid ? "bg-green-600 border-green-600" : "border-gray-400 bg-white"
                    )}>
                      {formData.offlineDepositPaid && <SafeIcon icon={FiIcons.FiCheck} className="text-white text-xs" />}
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-900 cursor-pointer">
                        Deposit paid offline/in-person
                      </label>
                      <p className="text-xs text-green-700 mt-1">Records the deposit as paid, confirms the booking instantly, and skips the online payment link.</p>
                    </div>
                  </div>
                  {formData.offlineDepositPaid && (
                    <div className="mt-4 pl-8">
                      <label className="block text-xs font-bold text-gray-700 mb-1">Deposit amount collected</label>
                      <div className="relative max-w-[200px]">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.offlineDepositAmount}
                          onChange={(e) => setFormData(p => ({ ...p, offlineDepositAmount: e.target.value }))}
                          className="w-full pl-7 pr-3 py-2 border-2 border-gray-300 rounded-lg font-bold text-gray-900 focus:border-green-500 focus:outline-none"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Pre-filled with the suggested deposit (50% of venue rental). Adjust if a different amount was collected.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className={cn(
        isModal
          ? "sticky bottom-0 p-4 bg-white border-t border-gray-200 z-10 -mx-1"
          : "fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
      )}>
        <div className={cn("flex gap-3", !isModal && "max-w-2xl mx-auto")}>
          {currentStep > 0 && (
            <Button variant="outline" className="flex-1 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-bold" onClick={handleBack}>
              <SafeIcon icon={FiIcons.FiChevronLeft} className="mr-1" /> Back
            </Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button
              className="flex-1 font-bold shadow-lg text-white"
              onClick={handleNext}
              disabled={
                (currentStep === 0 && !formData.eventType) ||
                (currentStep === 1 && !isDateTimeValid()) ||
                (currentStep === 2 && !isAddOnsValid())
              }
            >
              Next Step <SafeIcon icon={FiIcons.FiChevronRight} className="ml-1" />
            </Button>
          ) : (
            <Button
              className="flex-1 font-bold shadow-lg text-white"
              onClick={handleSubmit}
              isLoading={loading || redirectingToPayment}
              disabled={!isReviewStepValid()}
            >
              {redirectingToPayment
                ? 'Redirecting to Payment...'
                : loading
                  ? 'Submitting...'
                  : formData.offlineDepositPaid
                    ? 'Confirm Booking'
                    : formData.isOfflinePayment
                      ? 'Confirm Booking'
                      : (formData.eventType?.requiresAdminApproval !== false ? 'Submit for Approval' : 'Submit Booking')}
            </Button>
          )}
        </div>
      </div>

      {showPolicies && <PoliciesModal />}
    </div>
  );
};

export default BookingWizard;
